/**
 * ModalManager.ts — Guaranteed-dismissal modal wrapper.
 *
 * Wraps TooltipSystem.render() with a blocking waitForDismiss() that
 * ensures every popup has a dismiss handler. Prevents soft-lock bugs
 * where tooltips render without an input listener.
 */

import type { TooltipSystem, TooltipDefinition, TooltipOptions } from './TooltipSystem';
import type { InputManager, InputEvent } from './InputManager';

export interface ModalManagerOptions {
  tooltips: TooltipSystem;
  inputManager: InputManager;
}

export class ModalManager {
  private tooltips: TooltipSystem;
  private inputManager: InputManager;

  constructor(options: ModalManagerOptions) {
    this.tooltips = options.tooltips;
    this.inputManager = options.inputManager;
  }

  /**
   * Show a tooltip modal with guaranteed dismissal.
   * Renders tooltip content, waits for any key (including ESC), then clears.
   */
  async showTooltip(
    tooltip: TooltipDefinition,
    options?: TooltipOptions,
  ): Promise<void> {
    this.tooltips.render(tooltip, options);
    await this.waitForDismiss();
    this.tooltips.clear();
  }

  /**
   * Wait for any key press using InputManager.
   * Timeout after 120s as ultimate safety net.
   *
   * Uses a delayed start to avoid race conditions with MenuSystem.drain(),
   * which temporarily sets mode to 'disabled' on the next animation frame.
   * We wait one frame before setting anyKey mode so drain() completes first.
   */
  private waitForDismiss(): Promise<void> {
    return new Promise((resolve) => {
      // Double-rAF to guarantee execution after MenuSystem.drain() completes.
      // drain() uses a single rAF to restore mode; we need to run after that.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const previousMode = this.inputManager.getMode();
          this.inputManager.setMode('anyKey');

          const timeout = setTimeout(() => {
            cleanup();
            resolve();
          }, 120_000);

          const handler = (event: InputEvent) => {
            if (event.type === 'anyKey') {
              cleanup();
              resolve();
            }
          };

          const cleanup = () => {
            clearTimeout(timeout);
            this.inputManager.off(handler);
            this.inputManager.setMode(previousMode);
          };

          this.inputManager.on(handler);
        });
      });
    });
  }
}
